<template>
  <button
    :id="id"
    class="btn"
    :class="[
      `btn-size_${size}`,
      `btn-type_${type}`,
      {
        'btn-fill': fill,
        'btn_icon-btn': !!icon,
        'btn-overlay': overlay,
        'btn-max': max,
        'btn-loading': loading,
        'btn-rewrite-color': !!rewriteColor,

        btn_disabled: disabled,
      },
      `btn-custom-color_${customColor}`,
    ]"
    :disabled="disabled"
    :type="actionType"
    :style="cssProps"
    v-on="$listeners"
  >
    <div class="btn-container flex-centering">
      <Spinner v-show="loading" :size="size" :color="spinnerColor" />
      <div v-show="!loading" class="d-contents">
        <component :is="leftIconComponent" class="btn-icon btn-icon_left" />
        <component
          :is="onlyIcon"
          class="btn-icon btn-icon_center"
          :class="{ 'btn-icon_rotated': reverseIcon }"
        />
        <slot />
        <component :is="rightIconComponent" class="btn-icon btn-icon_right" />
      </div>
    </div>
  </button>
</template>

<script>
  import Spinner from '@/components/ui/Spinner';

  export default {
    name: 'Button',
    components: {
      Spinner,
    },
    data() {
      return {
        rightIconComponent: this.rightIcon
          ? () => import(`@/assets/icons/component/Icon=${this.rightIcon}.svg`)
          : null,
        leftIconComponent: this.leftIcon
          ? () => import(`@/assets/icons/component/Icon=${this.leftIcon}.svg`)
          : null,
        onlyIcon: this.icon ? () => import(`@/assets/icons/component/Icon=${this.icon}.svg`) : null,
      };
    },
    computed: {
      spinnerColor() {
        return this.type === 'primary' || (this.type !== 'primary' && this.overlay)
          ? 'white'
          : 'black';
      },
      cssProps() {
        return {
          '--rewrite-color': this.rewriteColor,
        };
      },
    },
    props: {
      /**
       * Button type
       */
      type: {
        type: String,
        default: 'primary',
        validator: (type) => ['primary', 'secondary', 'ghost', 'func'].includes(type),
      },
      size: {
        type: String,
        default: 'medium',
        validator: (size) => ['large', 'medium', 'small'].includes(size),
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      loading: {
        type: Boolean,
        default: false,
      },
      fill: {
        type: Boolean,
        default: false,
      },
      actionType: {
        type: String,
        default: 'button',
        validator: (type) => ['button', 'reset', 'submit'].includes(type),
      },
      id: [Number, String],
      rightIcon: String,
      leftIcon: String,
      icon: String,
      overlay: {
        type: Boolean,
        default: false,
      },
      reverseIcon: {
        type: Boolean,
        default: false,
      },
      customColor: {
        type: String,
        default: '',
      },
      rewriteColor: String,
      max: {
        type: Boolean,
        default: false,
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import 'src/styles/colors';

  .btn[type='submit']:not(.btn-fill) {
    margin: initial;
    width: auto !important;
  }

  .btn-icon_rotated {
    transform: rotate(180deg);
  }

  .btn-size_large,
  .btn-size_medium {
    .btn-container {
      gap: 8px;
    }
  }

  .btn-size_large .btn-container .btn-icon {
    height: 24px;
    width: 24px;
  }

  .btn-size_medium .btn-container .btn-icon {
    height: 20px;
    width: 20px;
  }

  .btn-size_small .btn-container .btn-icon {
    height: 10px;
    width: 10px;
  }

  .btn-size_small,
  .btn-type_func {
    .btn-container {
      gap: 4px;
      .btn-icon {
        height: 16px;
        width: 16px;
      }
    }
  }

  .btn-fill {
    width: 100% !important;
  }

  .btn-max {
    width: max-content;
  }

  .btn {
    &_disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    border-radius: 8px;
    font-weight: 500;
    border: 1px solid transparent;
    .btn-icon__svg {
      display: flex;
    }
    &:focus {
      outline: none;
      box-shadow: none;
    }
    &-size {
      &_large {
        padding: 15px 19px;
        font-size: 16px;
        &.btn_icon-btn {
          padding: 16px;
        }
      }
      &_medium {
        padding: 13px 17px;
        &.btn_icon-btn {
          padding: 14px;
        }
      }
      &_small {
        padding: 6px 12px;
        &.btn_icon-btn {
          padding: 7px;
        }
      }
    }
    &-type {
      &_primary {
        &:not(.btn-overlay) {
          background-color: $primary500;
          color: $white;
          & * {
            fill: white;
          }
          &:hover {
            background-color: $primary600;
          }
          &:active {
            background-color: $primary700;
          }
          &:disabled {
            background-color: $grey50;
            color: $grey300;
            & * {
              fill: $grey300;
            }
          }
        }
        &.btn-overlay {
          background-color: $surface-default;
          color: $text-primary;
          & * {
            fill: $icon-primary;
          }
          &:hover {
            background-color: $surface1-selected;
          }
          &:disabled {
            background-color: $surface-disabled;
            color: $text-tertiary;
            & * {
              fill: $text-tertiary;
            }
          }
        }
      }
      &_secondary {
        &:not(.btn-overlay) {
          background-color: $surface2;
          color: $text-primary;
          & * {
            fill: $text-primary;
          }
          &:hover {
            background-color: $surface2-selected;
          }
          &:disabled {
            background-color: $surface-disabled;
            color: $text-tertiary;
            & * {
              fill: $icon-secondary;
            }
          }
        }
        &.btn-overlay {
          background-color: $surface-oncolor-default;
          color: white;
          & * {
            fill: white;
          }
          &:hover {
            background-color: $surface-oncolor-selected;
          }
          &:disabled {
            background-color: $surface-disabled;
            color: $text-tertiary;
            & * {
              fill: $text-tertiary;
            }
          }
        }
      }
      &_ghost {
        background-color: transparent;
        &:not(.btn-overlay) {
          color: $text-primary;
          & * {
            fill: $text-primary;
          }
          &:hover {
            background-color: $surface1-selected;
          }
          &:disabled {
            color: $text-tertiary;
            & * {
              fill: $icon-secondary;
            }
          }
        }
        &.btn-overlay {
          color: $text-oncolor;
          &:hover,
          &:disabled,
          &.btn-loading {
            background-color: $surface-oncolor-selected;
          }
          &:disabled {
            color: $text-tertiary;
          }
        }
      }
      &_func {
        padding: 0 !important;
        border: 0;
        border-radius: 0;
        &:not(.btn-overlay) {
          color: $text-primary;
          & * {
            fill: $text-primary;
          }
          &:hover {
            color: $text-secondary;
            & * {
              fill: $text-secondary;
            }
          }
          &:disabled {
            color: $text-tertiary;
            & * {
              fill: $text-tertiary;
            }
          }
        }
        &.btn-overlay {
          color: $text-oncolor;
          &:hover {
            opacity: 0.8;
            & * {
              opacity: 0.8;
            }
          }
          &:disabled {
            color: $text-tertiary;
            & * {
              fill: $text-tertiary;
            }
          }
        }
      }
    }
    &-rewrite-color {
      background-color: var(--rewrite-color) !important;
    }
    &-custom-color {
      &_blue {
        color: $text-accent !important;
        svg {
          fill: $text-accent !important;
          path {
            fill: $text-accent !important;
          }
        }
      }
    }
  }
</style>
